/* ---------------------------------------------------------------------------
   ALP — FIND YOUR CAN  ·  shared state + commerce bus
   ---------------------------------------------------------------------------
   The "Find your can" page is split into independent sections (hero, strength,
   flavour, result, £1 trial, note). They must not know about each other, so
   they all talk to this one small store instead.

   Load order does not matter. Every section registers through a queue:

       (window.ALPFindQueue = window.ALPFindQueue || []).push(function (API) {
         // API is ready here
       });

   Nothing in here writes to the URL. Deep-link parameters are read once
   (?mg=12&flavour=spearmint) and left untouched afterwards, so gclid and any
   other ad parameter survive the visit intact.
--------------------------------------------------------------------------- */
(function () {
  "use strict";

  if (window.ALPFind) return;

  var ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";

  /* ---------- helpers ---------------------------------------------------- */

  // "6 mg" / "6mg" / "6-MG" all normalise to "6mg" so authors can type either.
  function norm(value) {
    return String(value == null ? "" : value)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  }

  function money(cents, options) {
    var opts = options || {};
    var currency =
      opts.currency ||
      (window.Shopify &&
        window.Shopify.currency &&
        window.Shopify.currency.active) ||
      "GBP";
    var locale =
      opts.locale || (window.Shopify && window.Shopify.locale) || "en-GB";
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency,
        minimumFractionDigits: opts.decimals == null ? 2 : opts.decimals,
        maximumFractionDigits: opts.decimals == null ? 2 : opts.decimals,
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(opts.decimals == null ? 2 : opts.decimals);
    }
  }

  // Per-pouch price. Under one major unit it is shown in sub-units ("29.95p")
  // when a sub-unit symbol is configured, otherwise as ordinary money.
  function perUnit(cents, count, options) {
    var opts = options || {};
    if (!count || count < 1) return "";
    var each = cents / count;
    var decimals = opts.decimals == null ? 2 : opts.decimals;
    if (opts.subunitSymbol && each < 100) {
      var text = each.toFixed(decimals);
      if (text.indexOf(".") > -1)
        text = text.replace(/0+$/, "").replace(/\.$/, "");
      return text + opts.subunitSymbol;
    }
    return money(each, {
      decimals: decimals,
      currency: opts.currency,
      locale: opts.locale,
    });
  }

  // {flavour} · {mg} mg  →  Chilled Mint · 6 mg
  function template(str, values) {
    if (!str) return "";
    return String(str).replace(/\{([a-z_]+)\}/gi, function (match, key) {
      var value = values[key];
      return value == null ? "" : String(value);
    });
  }

  function dataLayerPush(payload) {
    if (!payload) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  /* ---------- store ------------------------------------------------------ */

  var listeners = [];

  var API = {
    version: 1,
    root: ROOT,

    /* current selection */
    state: { mg: null, flavour: null },

    /* registered by the strength section: [{ key, label, mg, match }] */
    strengths: [],

    /* registered by the flavour section: key -> flavour record */
    flavours: {},

    /* registered by the £1 trial section (null when the section is removed) */
    trial: null,

    /* set by the result section — money formatting, tracking, cart behaviour */
    config: {},

    /* utilities the sections reuse */
    norm: norm,
    money: money,
    perUnit: perUnit,
    template: template,

    /* ---- subscription ---- */
    on: function (fn) {
      if (typeof fn === "function" && listeners.indexOf(fn) === -1)
        listeners.push(fn);
      return fn;
    },
    off: function (fn) {
      var i = listeners.indexOf(fn);
      if (i > -1) listeners.splice(i, 1);
    },
    emit: function (reason) {
      var detail = {
        reason: reason || "change",
        state: { mg: API.state.mg, flavour: API.state.flavour },
        match: API.resolve(),
      };
      for (var i = 0; i < listeners.length; i++) {
        try {
          listeners[i](detail);
        } catch (e) {
          /* one bad listener must not stop the rest */
        }
      }
      document.dispatchEvent(
        new CustomEvent("alp-find:change", { bubbles: true, detail: detail }),
      );
      return detail;
    },

    /* ---- registration ---- */
    registerStrengths: function (list) {
      API.strengths = Array.isArray(list) ? list : [];
      API.emit("strengths");
    },

    registerFlavours: function (map) {
      API.flavours = map || {};
      API.emit("flavours");
    },

    registerTrial: function (record) {
      API.trial = record || null;
      API.emit("trial");
    },

    /* ---- selection ---- */
    select: function (key, value) {
      if (key !== "mg" && key !== "flavour") return;
      if (API.state[key] === value) return API.emit("select");
      API.state[key] = value;
      return API.emit("select");
    },

    clear: function () {
      API.state.mg = null;
      API.state.flavour = null;
      return API.emit("clear");
    },

    getStrength: function (mgKey) {
      var key = mgKey == null ? API.state.mg : mgKey;
      if (key == null) return null;
      for (var i = 0; i < API.strengths.length; i++) {
        if (String(API.strengths[i].key) === String(key))
          return API.strengths[i];
      }
      return null;
    },

    getFlavour: function (flavourKey) {
      var key = flavourKey == null ? API.state.flavour : flavourKey;
      if (key == null) return null;
      return API.flavours[key] || null;
    },

    /* Flavour + strength → the exact variant to sell.
       1. an explicit variant ID set on the flavour block wins
       2. otherwise match the strength's option value ("6mg")
       3. otherwise fall back to the variant title containing it            */
    findVariant: function (flavour, strength) {
      if (!flavour || !strength) return null;
      var variants = flavour.variants || [];
      var wanted = norm(strength.match || strength.key);

      // An override only counts when it names a variant this product actually
      // has. A stale ID must never be fabricated into a line item — it would
      // pass every availability check here and then be rejected by the cart.
      var explicit =
        flavour.variant_by_strength &&
        flavour.variant_by_strength[String(strength.key)];
      if (explicit) {
        for (var e = 0; e < variants.length; e++) {
          if (String(variants[e].id) === String(explicit)) return variants[e];
        }
        if (window.console && console.warn) {
          console.warn(
            "[alp-find] Variant override " +
              explicit +
              ' for "' +
              flavour.title +
              '" at ' +
              strength.key +
              " is not a variant of that product — ignoring it.",
          );
        }
      }

      var i, j;
      for (i = 0; i < variants.length; i++) {
        var options = variants[i].options || [];
        for (j = 0; j < options.length; j++) {
          if (norm(options[j]) === wanted) return variants[i];
        }
      }
      for (i = 0; i < variants.length; i++) {
        if (norm(variants[i].title).indexOf(wanted) > -1) return variants[i];
      }
      return null;
    },

    /* Which tin to show for the current pick, most specific first:
         1. a per-strength override on the flavour block
         2. the matched variant's own image in Shopify
         3. the product image                                             */
    imagesFor: function (match) {
      if (!match) return null;
      var byStrength = match.flavour.images_by_strength || {};
      var override = byStrength[String(match.strength.key)];
      if (override && override.result) return override;

      var variant = match.variant;
      if (API.config.use_variant_image !== false && variant && variant.image) {
        return { result: variant.image, result_2x: variant.image_2x || "" };
      }
      return match.flavour.images || {};
    },

    /* The full picture the result card renders from. */
    resolve: function () {
      var flavour = API.getFlavour();
      var strength = API.getStrength();
      if (!flavour || !strength) return null;
      var variant = API.findVariant(flavour, strength);
      return { flavour: flavour, strength: strength, variant: variant };
    },

    /* ---- commerce ---- */

    // Adds the matched can, plus the £1 trial when it is ticked. Both go in one
    // request so the trial can never end up in the cart on its own.
    addToCart: function (options) {
      var opts = options || {};
      var match = API.resolve();
      if (!match || !match.variant || !match.variant.id) {
        return Promise.reject(
          new Error("alp-find: no variant for the current selection"),
        );
      }
      return API.addItems(
        [
          {
            id: Number(match.variant.id),
            quantity: Number(opts.quantity || 1),
          },
        ],
        { match: match },
      );
    },

    // Adds any number of lines in ONE request — used by the bundle builder, and
    // by addToCart above. The £1 trial rides along in the same request, so it
    // can never end up in the cart on its own.
    addItems: function (lines, options) {
      var opts = options || {};
      var items = (lines || [])
        .filter(function (line) {
          return line && line.id && Number(line.quantity) > 0;
        })
        .map(function (line) {
          return { id: Number(line.id), quantity: Number(line.quantity) };
        });

      if (!items.length)
        return Promise.reject(new Error("alp-find: nothing to add"));

      var trial = API.trial;
      if (trial && trial.enabled && trial.checked && trial.variant_id) {
        var trialItem = { id: Number(trial.variant_id), quantity: 1 };
        if (trial.property_key) {
          trialItem.properties = {};
          trialItem.properties[trial.property_key] = "true";
        }
        items.push(trialItem);
      }

      return fetch(ROOT + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items: items }),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) {
              // The payload is logged so a rejected line can be traced back to
              // the exact variant ID that was sent.
              if (window.console && console.error) {
                console.error("[alp-find] cart/add rejected", {
                  status: response.status,
                  shopify: (data && data.description) || data,
                  sent: items,
                  resolved: items.map(function (line) {
                    var found = API.lookupVariant(line.id);
                    return found
                      ? found.flavour.title +
                          " / " +
                          found.variant.title +
                          " (" +
                          line.id +
                          ") available=" +
                          found.variant.available
                      : "UNKNOWN VARIANT " +
                          line.id +
                          " — not in this page's catalogue";
                  }),
                });
              }
              throw new Error((data && data.description) || "cart/add failed");
            }
            return data;
          });
        })
        .then(function (data) {
          if (opts.match) {
            API.track("add_to_cart", opts.match, {
              quantity: items[0].quantity,
            });
          } else {
            API.trackBundle("add_to_cart", items);
          }
          return data;
        });
    },

    refreshCart: function (drawerSectionId) {
      var done = Promise.resolve();
      if (drawerSectionId) {
        done = fetch(ROOT + "?sections=" + encodeURIComponent(drawerSectionId))
          .then(function (r) {
            return r.json();
          })
          .then(function (data) {
            var html = data[drawerSectionId];
            var host = document.getElementById(
              "shopify-section-" + drawerSectionId,
            );
            if (!html || !host) return;
            var doc = new DOMParser().parseFromString(html, "text/html");
            var inner = doc.getElementById(
              "shopify-section-" + drawerSectionId,
            );
            host.innerHTML = inner ? inner.innerHTML : html;
          })
          .catch(function () {
            /* the cart:refresh below is enough on its own */
          });
      }
      return done.then(function () {
        document.dispatchEvent(
          new CustomEvent("cart:refresh", { bubbles: true }),
        );
      });
    },

    openCartDrawer: function () {
      var drawer = document.getElementById("cart-drawer");
      if (drawer) {
        if (typeof drawer.show === "function") {
          drawer.show();
          return true;
        }
        if (typeof drawer.showDialog === "function") {
          drawer.showDialog();
          return true;
        }
      }
      var toggle = document.querySelector('[aria-controls="cart-drawer"]');
      if (toggle) {
        toggle.click();
        return true;
      }
      return false;
    },

    // Every line the bundle builder holds, resolved back to flavour + strength
    // so the dataLayer gets real product names rather than bare variant IDs.
    trackBundle: function (event, items) {
      if (!API.config.tracking || !items || !items.length) return;
      var currency =
        (window.Shopify &&
          window.Shopify.currency &&
          window.Shopify.currency.active) ||
        "GBP";
      var value = 0;
      var payload = [];

      items.forEach(function (line) {
        var found = API.lookupVariant(line.id);
        var price = found && found.variant ? found.variant.price : 0;
        value += (price / 100) * line.quantity;
        payload.push({
          item_id: String(
            (found && found.variant && found.variant.sku) || line.id,
          ),
          item_name: found ? found.flavour.title : String(line.id),
          item_variant: found && found.variant ? found.variant.title : "",
          price: price / 100,
          quantity: line.quantity,
        });
      });

      dataLayerPush({
        event: event,
        ecommerce: { currency: currency, value: value, items: payload },
      });
    },

    // variant id -> { flavour, variant }, searching the registered catalogue.
    lookupVariant: function (variantId) {
      for (var key in API.flavours) {
        if (!Object.prototype.hasOwnProperty.call(API.flavours, key)) continue;
        var flavour = API.flavours[key];
        var variants = flavour.variants || [];
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === String(variantId))
            return { flavour: flavour, variant: variants[i] };
        }
      }
      return null;
    },

    /* ---- tracking ----
       view_item when the result renders, add_to_cart on the CTA. Nothing else,
       and never anything that touches the URL. */
    track: function (event, match, extra) {
      if (!API.config.tracking || !match || !match.variant) return;
      var currency =
        (window.Shopify &&
          window.Shopify.currency &&
          window.Shopify.currency.active) ||
        "GBP";
      var value = (match.variant.price || 0) / 100;
      var item = {
        item_id: String(match.variant.sku || match.variant.id),
        item_name: match.flavour.title,
        item_variant: match.strength.label + " " + (match.strength.unit || ""),
        price: value,
        quantity: (extra && extra.quantity) || 1,
      };
      dataLayerPush({
        event: event,
        ecommerce: {
          currency: currency,
          value: value * item.quantity,
          items: [
            item,
          ],
        },
      });
    },
  };

  window.ALPFind = API;

  /* ---------- deep links (read-only) ------------------------------------- */

  API.applyDeepLink = function () {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }

    var mg = params.get(API.config.param_strength || "mg");
    if (mg) {
      var strength = null;
      for (var i = 0; i < API.strengths.length; i++) {
        var candidate = API.strengths[i];
        if (
          norm(candidate.key) === norm(mg) ||
          norm(candidate.label) === norm(mg) ||
          norm(candidate.match) === norm(mg)
        ) {
          strength = candidate;
          break;
        }
      }
      if (strength) API.state.mg = strength.key;
    }

    var flavour = params.get(API.config.param_flavour || "flavour");
    if (flavour) {
      var target = norm(flavour);
      for (var key in API.flavours) {
        if (!Object.prototype.hasOwnProperty.call(API.flavours, key)) continue;
        var record = API.flavours[key];
        if (
          norm(key) === target ||
          norm(record.title) === target ||
          norm(record.handle) === target
        ) {
          API.state.flavour = key;
          break;
        }
      }
    }

    API.emit("deeplink");
  };

  /* ---------- drain the registration queue ------------------------------- */

  var queue = window.ALPFindQueue || [];
  window.ALPFindQueue = {
    push: function (fn) {
      if (typeof fn === "function") fn(API);
    },
  };
  for (var q = 0; q < queue.length; q++) {
    try {
      queue[q](API);
    } catch (e) {
      /* keep draining */
    }
  }

  // Deep links are applied after every section has had a chance to register.
  function boot() {
    API.applyDeepLink();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
